import { assertSafeIntegerInRange } from '@kenocyber/shared';
import { MAX_CANNON_LEVEL } from '../merge';
import { HUNT_BALANCE } from './balance';

/**
 * Cannon formation geometry, in SHIP-LOCAL coordinates.
 *
 * - `forward` grows toward the nose (the direction the ship is aiming).
 * - `lateral` grows to the ship's right; a mirrored pair shares `forward` and
 *   has opposite `lateral`.
 *
 * Keeping it local means the formation is defined once and simply rotated with
 * the ship, and it can be unit-tested without a renderer. Only the LEVEL
 * matters here - the rarity decides how many projectiles each cannon emits,
 * never how many cannons there are.
 *
 * The cannons are hardpoints ON the ship, not a ring around it: one ahead of
 * the nose plus a ladder of mirrored rungs stepping outwards and backwards
 * along the swept wing, so the battery follows the hull's own silhouette and
 * reads as installed hardware.
 *
 * | Level | Layout (centre = ahead of the nose, A/B/C = wing rungs) |
 * | ----- | ------------------------------------------------------- |
 * | 1     | centre                                                  |
 * | 2     | A                                                       |
 * | 3     | centre, B                                               |
 * | 4     | A, C                                                     |
 * | 5     | centre, B, C                                            |
 * | 6     | A, B, C                                                 |
 * | 7     | centre, A, B, C                                         |
 *
 * The positions themselves live in `HUNT_BALANCE.formation` (`centreDistance`,
 * `cannonRungs`, `cannonLevelRungs`); nothing here hard-codes one.
 */

export interface CannonSlot {
  /** Distance ahead of the ship centre, in arena units. */
  readonly forward: number;
  /** Distance to the ship's right; negative is left. Zero = on the axis. */
  readonly lateral: number;
}

/**
 * The slots for a cannon level, ordered centre-first then down the wing.
 *
 * Deterministic and side-effect free; the renderer maps each slot to a sprite
 * and the simulation maps each slot to a muzzle.
 */
export function cannonFormation(level: number): readonly CannonSlot[] {
  assertSafeIntegerInRange(level, 1, MAX_CANNON_LEVEL, 'cannon level');
  const { centreDistance, cannonRungs, cannonLevelRungs } = HUNT_BALANCE.formation;
  const rungIndices = cannonLevelRungs[level];
  if (!rungIndices) {
    throw new RangeError(`no cannon mounts configured for level ${level}`);
  }

  const slots: CannonSlot[] = [];
  // Odd levels put one cannon on the aim axis, ahead of the nose.
  if (level % 2 === 1) {
    slots.push({ forward: centreDistance, lateral: 0 });
  }
  for (const index of rungIndices) {
    const rung = cannonRungs[index];
    if (!rung) {
      throw new RangeError(`level ${level} refers to missing cannon rung ${index}`);
    }
    // Both sides come from the same rung, so a pair is exactly symmetric.
    slots.push(
      { forward: rung.forward, lateral: -rung.lateral },
      { forward: rung.forward, lateral: rung.lateral },
    );
  }
  return slots;
}

/**
 * **A MESMA formação, em proporções do casco** (gate V28I.AB).
 *
 * ## Porque ela existe
 *
 * Porque a bateria é a mesma nave nos dois modos, e as duas arenas medem em
 * unidades diferentes: a caça desenha um casco de `displayWidth` fixo, e um
 * quadrante mede em milli-unidades com um casco que **cresce com a vida
 * máxima**. Uma segunda tabela de posições seria uma segunda decisão sobre onde
 * ficam os canhões, e a primeira a divergir seria aquela em que ninguém tocou.
 *
 * Então o que atravessa é a FORMA: cada posição dividida pela meia-largura do
 * casco de referência, em basis points. Quem consome multiplica pelo seu próprio
 * raio de casco e fica com a mesma bateria à sua escala.
 *
 * **Nenhum número é copiado.** `centreDistance`, os degraus e o avanço da boca
 * continuam a viver em `HUNT_BALANCE.formation` e em mais lado nenhum; isto lê-os
 * e divide.
 *
 * A boca já vem incluída — é o ponto de onde o projétil sai, e é ele que
 * interessa a quem dispara.
 */
export interface CannonMountRatio {
  /** Para a frente, em basis points da meia-largura do casco de referência. */
  readonly forwardBps: number;
  /** Para a direita da mira, na mesma unidade. Um par espelhado troca o sinal. */
  readonly lateralBps: number;
}

/** A meia-largura do casco de referência: o denominador de todas as proporções. */
const REFERENCE_HALF_WIDTH = HUNT_BALANCE.ship.displayWidth / 2;

/**
 * As bocas da bateria deste nível, em proporções do casco.
 *
 * A ORDEM é a canónica de `cannonFormation` — o canhão central primeiro nos
 * níveis ímpares, depois cada degrau em pares espelhados — e é ela que faz duas
 * máquinas emitirem a mesma salva na mesma sequência.
 */
export function cannonMuzzleRatios(level: number): readonly CannonMountRatio[] {
  return cannonFormation(level)
    .map(slotMuzzle)
    .map((slot) => ({
      forwardBps: Math.round((slot.forward * 10_000) / REFERENCE_HALF_WIDTH),
      lateralBps: Math.round((slot.lateral * 10_000) / REFERENCE_HALF_WIDTH),
    }));
}

/**
 * **O espaçamento entre dois projéteis da MESMA boca**, na mesma proporção.
 *
 * Pequeno de propósito: os dois tiros de um canhão Perfect saem lado a lado da
 * mesma boca, e não em leque pela asa inteira. A decisão é
 * `HUNT_BALANCE.ship.projectileSpacing`, e isto é uma leitura dela.
 */
export const CANNON_PROJECTILE_SPACING_BPS = Math.round(
  (HUNT_BALANCE.ship.projectileSpacing * 10_000) / REFERENCE_HALF_WIDTH,
);

/** Muzzle of a slot: a little further forward than the cannon body itself. */
export function slotMuzzle(slot: CannonSlot): CannonSlot {
  return {
    forward: slot.forward + HUNT_BALANCE.formation.muzzleLead,
    lateral: slot.lateral,
  };
}

/**
 * Converts a ship-local slot into world coordinates for a given facing.
 *
 * `facing` is the world angle of the nose, so `forward` runs along it and
 * `lateral` along its right-hand perpendicular.
 */
export function slotToWorld(
  slot: CannonSlot,
  shipX: number,
  shipY: number,
  facing: number,
): { x: number; y: number } {
  const forwardX = Math.cos(facing);
  const forwardY = Math.sin(facing);
  // Right-hand perpendicular of (forwardX, forwardY).
  const rightX = -forwardY;
  const rightY = forwardX;
  return {
    x: shipX + forwardX * slot.forward + rightX * slot.lateral,
    y: shipY + forwardY * slot.forward + rightY * slot.lateral,
  };
}

/**
 * Do two mounts' artwork rectangles intersect?
 *
 * Every cannon is drawn at the ship's own rotation, so two of them are clear of
 * each other once they are a full art WIDTH apart across the hull or a full art
 * LENGTH apart along it. Measuring centre-to-centre against the square sprite
 * box would be the wrong test: it rejects hardpoints that are visibly clear,
 * which is exactly what a battery stacked down one wing looks like.
 */
export function slotArtOverlaps(a: CannonSlot, b: CannonSlot): boolean {
  const { cannonArtWidth, cannonArtLength } = HUNT_BALANCE.formation;
  return (
    Math.abs(a.lateral - b.lateral) < cannonArtWidth &&
    Math.abs(a.forward - b.forward) < cannonArtLength
  );
}

/** Smallest gap between any two cannons of a formation, in arena units. */
export function minimumSlotSeparation(level: number): number {
  const slots = cannonFormation(level);
  if (slots.length < 2) {
    return Number.POSITIVE_INFINITY;
  }
  let minimum = Number.POSITIVE_INFINITY;
  for (let a = 0; a < slots.length; a += 1) {
    for (let b = a + 1; b < slots.length; b += 1) {
      const first = slots[a];
      const second = slots[b];
      if (!first || !second) {
        continue;
      }
      minimum = Math.min(
        minimum,
        Math.hypot(first.forward - second.forward, first.lateral - second.lateral),
      );
    }
  }
  return minimum;
}

/** Furthest any cannon reaches from the ship centre, for arena-fit checks. */
export function formationReach(level: number): number {
  return cannonFormation(level).reduce(
    (max, slot) => Math.max(max, Math.hypot(slot.forward, slot.lateral)),
    0,
  );
}

/**
 * Largest lateral offset of a formation - half the width the battery occupies
 * across the ship's axis. This is the number that decides whether the cannons
 * read as attached hardware or as escorts flying alongside.
 */
export function formationHalfWidth(level: number): number {
  return cannonFormation(level).reduce((max, slot) => Math.max(max, Math.abs(slot.lateral)), 0);
}
